import { useConfirmModal } from '@hive/component';
import { WorkspaceDialogService } from '@hive/core/modules/dialogs';
import { type I18nString, useI18n } from '@hive/i18n';
import { InformationFillDuotoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import {
  SubscriptionService,
  UserQuotaService,
  WorkspaceSubscriptionService,
} from '../../modules/cloud';
import * as styles from '../../modules/quota/views/styles.css';

interface SubscriptionRequiredMessage {
  title: I18nString;
  description: I18nString;
  confirmText: I18nString;
  tips: I18nString[];
  cancelText: I18nString;
}

const message: SubscriptionRequiredMessage = {
  title: 'com.hive.payment.subscription-required.title',
  description: 'com.hive.payment.subscription-required.description',
  confirmText: 'com.hive.payment.subscription-required.confirm',
  tips: [
    'com.hive.payment.subscription-required.tips-1',
    'com.hive.payment.subscription-required.tips-2',
  ],
  cancelText: 'Cancel',
};

const quotaExceededMessage = {
  title: 'com.hive.payment.quota-exceeded.title',
  description: 'com.hive.payment.quota-exceeded.description',
  confirmText: 'com.hive.payment.quota-exceeded.confirm',
  cancelText: 'Cancel',
};

const Tips = ({ tips }: { tips: I18nString[] }) => {
  const t = useI18n();
  if (!tips || tips.length < 1) {
    return null;
  }
  return (
    <div className={styles.tipsStyle}>
      {tips.map(tip => (
        <div key={tip.toString()} className={styles.tipStyle}>
          <div className={styles.bullet} />
          {t.t(tip)}
        </div>
      ))}
    </div>
  );
};

const Title = ({ title }: { title: string }) => {
  return (
    <div className={styles.modalTitle}>
      <InformationFillDuotoneIcon className={styles.errorIcon} />
      {title}
    </div>
  );
};

export interface MonthlyQuotaStatus {
  docsCreated: number;
  boardsCreated: number;
  monthlyDocLimit: number;
  monthlyBoardLimit: number;
  docsRemaining: number;
  boardsRemaining: number;
  isUnlimited: boolean;
}

/**
 * Hook to check subscription status and show upgrade modal for free users.
 * Use this to block actions like creating docs, journals, collections, and folders
 * when the user doesn't have an active subscription.
 *
 * Checks both user-level subscriptions and workspace-level subscriptions (team trials).
 * Also checks monthly doc/board creation quotas for lifetime plans.
 */
export const useRequireSubscription = () => {
  const { openConfirmModal } = useConfirmModal();
  const subscriptionService = useService(SubscriptionService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const userQuotaService = useService(UserQuotaService);
  const workspaceDialogService = useService(WorkspaceDialogService);
  const t = useI18n();
  const [monthlyQuota, setMonthlyQuota] = useState<MonthlyQuotaStatus | null>(
    null
  );

  // Revalidate both user and workspace subscriptions on mount
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    workspaceSubscriptionService.subscription.revalidate();
    userQuotaService.quota.revalidate();
  }, [subscriptionService, workspaceSubscriptionService, userQuotaService]);

  // User-level subscription (personal plans)
  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const isUserSubLoading = useLiveData(
    subscriptionService.subscription.isRevalidating$
  );

  // Workspace-level subscription (team plans including trials)
  const workspaceSubscription = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );
  const isWorkspaceSubLoading = useLiveData(
    workspaceSubscriptionService.subscription.isRevalidating$
  );

  // Check if user has a paid plan (including lifetime)
  const hasPaidPlan = useLiveData(userQuotaService.quota.hasPaidPlan$);
  const isQuotaLoading = useLiveData(userQuotaService.quota.isRevalidating$);

  const isLoading = isUserSubLoading || isWorkspaceSubLoading || isQuotaLoading;

  // User has no subscription (free plan)
  // Check both user-level and workspace-level subscriptions AND quota-based plans
  // User is "free" only when they have neither a subscription nor a paid quota plan
  const isFreeUser =
    !proSubscription && !workspaceSubscription && !hasPaidPlan && !isLoading;

  const showUpgradeModal = useCallback(() => {
    openConfirmModal({
      title: <Title title={t.t(message.title)} />,
      description: t.t(message.description),
      confirmText: t.t(message.confirmText),
      cancelText: t.t(message.cancelText),
      children: <Tips tips={message.tips} />,
      childrenContentClassName: styles.modalChildren,
      onConfirm: () => {
        workspaceDialogService.open('setting', {
          activeTab: 'plans',
          scrollAnchor: 'cloudPricingPlan',
        });
      },
      confirmButtonOptions: {
        variant: 'primary',
      },
    });
  }, [openConfirmModal, t, workspaceDialogService]);

  const showQuotaExceededModal = useCallback(
    (type: 'doc' | 'board', currentCount: number, limit: number) => {
      openConfirmModal({
        title: (
          <Title
            title={t.t(quotaExceededMessage.title, {
              type: type === 'doc' ? 'document' : 'board',
            })}
          />
        ),
        description: t.t(quotaExceededMessage.description, {
          type: type === 'doc' ? 'document' : 'board',
          current: currentCount,
          limit: limit,
        }),
        confirmText: t.t(quotaExceededMessage.confirmText),
        cancelText: t.t(quotaExceededMessage.cancelText),
        childrenContentClassName: styles.modalChildren,
        onConfirm: () => {
          workspaceDialogService.open('setting', {
            activeTab: 'plans',
            scrollAnchor: 'cloudPricingPlan',
          });
        },
        confirmButtonOptions: {
          variant: 'primary',
        },
      });
    },
    [openConfirmModal, t, workspaceDialogService]
  );

  /**
   * Wraps an action to check subscription status first.
   * If user is on free plan, shows upgrade modal instead of executing the action.
   * @param action - The action to execute if user has subscription
   * @returns A wrapped function that checks subscription before executing
   */
  const requireSubscription = useCallback(
    <T extends (...args: any[]) => any>(action: T): T => {
      return ((...args: Parameters<T>) => {
        if (isFreeUser) {
          showUpgradeModal();
          return;
        }
        return action(...args);
      }) as T;
    },
    [isFreeUser, showUpgradeModal]
  );

  /**
   * Check if user can create a doc based on their monthly quota.
   * Returns true if allowed, false if quota exceeded (and shows modal).
   */
  const canCreateDoc = useCallback((): boolean => {
    if (!monthlyQuota) {
      return true; // Allow if quota not loaded yet
    }
    if (monthlyQuota.isUnlimited || monthlyQuota.monthlyDocLimit === -1) {
      return true;
    }
    if (monthlyQuota.docsRemaining <= 0) {
      showQuotaExceededModal(
        'doc',
        monthlyQuota.docsCreated,
        monthlyQuota.monthlyDocLimit
      );
      return false;
    }
    return true;
  }, [monthlyQuota, showQuotaExceededModal]);

  /**
   * Check if user can create a board based on their monthly quota.
   * Returns true if allowed, false if quota exceeded (and shows modal).
   */
  const canCreateBoard = useCallback((): boolean => {
    if (!monthlyQuota) {
      return true; // Allow if quota not loaded yet
    }
    if (monthlyQuota.isUnlimited || monthlyQuota.monthlyBoardLimit === -1) {
      return true;
    }
    if (monthlyQuota.boardsRemaining <= 0) {
      showQuotaExceededModal(
        'board',
        monthlyQuota.boardsCreated,
        monthlyQuota.monthlyBoardLimit
      );
      return false;
    }
    return true;
  }, [monthlyQuota, showQuotaExceededModal]);

  /**
   * Refresh the monthly quota status from the server.
   */
  const refreshMonthlyQuota = useCallback(async () => {
    // Monthly quota will be fetched via GraphQL when available
    // For now, we set it as unlimited by default
    setMonthlyQuota({
      docsCreated: 0,
      boardsCreated: 0,
      monthlyDocLimit: -1,
      monthlyBoardLimit: -1,
      docsRemaining: Infinity,
      boardsRemaining: Infinity,
      isUnlimited: true,
    });
  }, []);

  return {
    isFreeUser,
    isLoading,
    showUpgradeModal,
    requireSubscription,
    monthlyQuota,
    canCreateDoc,
    canCreateBoard,
    refreshMonthlyQuota,
    showQuotaExceededModal,
  };
};
